import { getWindowContext } from '../core/pre-config/pre-config.utils';

/**
 * Sprawdza czy element znajduje się w widoku użytkownika.
 *
 * @param {HTMLElement} el - kontener z elementem do sprawdzenia

 * @returns {boolean}
 */
export const isElemInView = (el: HTMLElement): boolean => {
    if (!el) return false;
    const document = getWindowContext().document;
    const rect: DOMRect = el.getBoundingClientRect();
    if (rect.top === 0 && rect.bottom === 0) return false;

    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const isInView = rect.top >= 0 && rect.bottom <= viewportHeight;

    if (!isInView) return false;

    // dodatkowo sprawdzenie czy element nie jest przysłonięty
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const topElem = document.elementFromPoint(centerX, centerY);
    if (!topElem) return false;

    // AdConsole nad slotami tworzy overlay z informacjami, które przysłania element
    if (
        topElem.id.startsWith('uanc_slot_info_') ||
        (topElem instanceof HTMLElement && topElem.offsetParent?.id.startsWith('uanc_slot_info_'))
    ) {
        return true;
    }

    return el === topElem || el.contains(topElem);
};

/**
 * Szuka najbliższego rodzica (elementu DOM), który ma ustawioną właściwość display='block'.
 *
 * @param {HTMLElement} element - elementem do sprawdzenia
 * @returns {HTMLElement | null} najbliższy parent element, który jest widoczny
 */
const findVisibleParent = (element: HTMLElement): HTMLElement | null => {
    let currentElement: HTMLElement | null = element;
    while (currentElement) {
        const display = getComputedStyle(currentElement).display;

        if (display !== 'none') return currentElement;
        if (!currentElement.parentElement) break;

        currentElement = currentElement.parentElement;
    }
    return null;
};

/**
 * Monitoruje widoczność przekazanego elementu w obrębie viewport użytkownika.
 * Wykorzystuje IntersectionObserver, aby określić moment pojawienia się elementu na ekranie (uwzględnia ustawienia lazyLoadingu).
 *
 * @param {HTMLElement} element - element do śledzenia przez observer
 * @param {() => void} cb - callback do wywołania gdy element znajdzie się w viewport
 * @param {number} lazyLoadingValue - opcjonalnie, wartość lazy loadingu
 * @returns {void}
 */
export const observeElement = (element: HTMLElement, cb: () => void, lazyLoadingValue: number = 0): void => {
    let observeElement: HTMLElement | null = element;
    const isElementNotVisible = observeElement && getComputedStyle(observeElement).display === 'none';
    if (isElementNotVisible) {
        observeElement = findVisibleParent(observeElement);
    }
    if (observeElement) {
        const settings = {
            root: observeElement.dataset.adRoot ? getWindowContext().document.querySelector(observeElement.dataset.adRoot) : null,
            rootMargin: `${lazyLoadingValue}px 0px ${lazyLoadingValue}px 0px`,
        };

        const observer: IntersectionObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    observer.unobserve(entry.target);
                    cb();
                }
            }
        }, settings);

        observer.observe(observeElement);
    }
};
